import { FC, useMemo } from "react";

import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";

import { fillFor, formatMargin, formatPercent, STRIPE } from "./colors";
import InfoTip from "./InfoTip";
import { shortName } from "./survey";
import type { Call, Candidate, Office } from "./types";
import { OFFICE_LABEL, RaceRow } from "./view";

interface RaceListProps {
  office: Office;
  onSelect: (state: string) => void;
  rows: RaceRow[];
  sourceLabel: string | null;
}

/** Closest races first; races with no reading at the bottom. */
const sortRows = (rows: RaceRow[]) =>
  [...rows].sort((a, b) => {
    const pa = a.call.leftProbability;
    const pb = b.call.leftProbability;
    if (pa === null && pb === null)
      return a.state.name.localeCompare(b.state.name);
    if (pa === null) return 1;
    if (pb === null) return -1;
    return Math.abs(pa - 0.5) - Math.abs(pb - 0.5);
  });

const nameOf = (candidate: Candidate | null) =>
  candidate ? shortName(candidate) : "TBD";

/** Map color of a race; projected flips get the same 45° stripes. */
export const Swatch: FC<{ call: Call; flip: boolean }> = ({ call, flip }) => (
  <Box
    className="w-3.5 h-3.5 rounded-sm overflow-hidden"
    style={{ backgroundColor: fillFor(call) }}
  >
    {flip &&
      [-3, 3].map((offset) => (
        <Box
          className="absolute"
          key={offset}
          style={{
            backgroundColor: STRIPE,
            height: 2,
            left: -3,
            top: 6 + offset,
            transform: [{ rotate: "-45deg" }],
            width: 20,
          }}
        />
      ))}
  </Box>
);

const RaceList: FC<RaceListProps> = ({
  office,
  onSelect,
  rows,
  sourceLabel,
}) => {
  const sorted = useMemo(() => sortRows(rows), [rows]);

  return (
    <Card className="p-5 rounded-2xl border border-outline-200">
      <VStack className="gap-3">
        <HStack className="items-center gap-1">
          <Text className="text-sm font-semibold text-typography-900">
            {office === "house"
              ? "Generic ballot by state"
              : `${OFFICE_LABEL[office]} races, closest first`}
          </Text>
          <InfoTip text="Only seats on the 2026 ballot are listed. A striped swatch and the FLIP tag mark races where the projected leader differs from the party holding the seat. Click a row to open the state and its county map." />
        </HStack>

        <HStack className="px-1 gap-2">
          <Text className="flex-1 text-xs text-typography-400">Race</Text>
          <Text className="w-20 text-right text-xs text-typography-400">
            Foresight
          </Text>
          {sourceLabel && (
            <Text
              className="w-20 text-right text-xs text-typography-400"
              numberOfLines={1}
            >
              {sourceLabel}
            </Text>
          )}
        </HStack>

        <VStack className="gap-0.5">
          {sorted.map((row) => (
            <Pressable
              className="flex-row items-center gap-2 px-1 py-1.5 rounded-md hover:bg-background-50"
              key={`${row.state.abbr}-${row.race?.special ? "special" : "regular"}`}
              onPress={() => onSelect(row.state.abbr)}
            >
              <Swatch call={row.call} flip={row.flip} />
              <VStack className="flex-1 min-w-0">
                <HStack className="items-center gap-1.5">
                  <Text
                    className="text-sm font-medium text-typography-900"
                    numberOfLines={1}
                  >
                    {row.state.name}
                  </Text>
                  {row.flip && (
                    <Text className="text-2xs font-bold tracking-wide text-typography-700 border border-outline-300 rounded px-1">
                      FLIP
                    </Text>
                  )}
                </HStack>
                {row.race && (
                  <Text
                    className="text-xs text-typography-500"
                    numberOfLines={1}
                  >
                    {nameOf(row.race.left)} ({row.leftParty}) vs{" "}
                    {nameOf(row.race.right)} (R) · held by{" "}
                    {row.race.incumbentParty}
                  </Text>
                )}
              </VStack>
              <VStack className="w-20 items-end">
                <Text className="text-sm font-semibold text-typography-900">
                  {row.estimate
                    ? formatMargin(row.estimate.margin, row.leftParty)
                    : "—"}
                </Text>
                {row.estimate && (
                  <Text className="text-xs text-typography-500">
                    {formatPercent(row.estimate.leftProbability)}{" "}
                    {row.leftParty}
                  </Text>
                )}
              </VStack>
              {sourceLabel && (
                <Text className="w-20 text-right text-sm text-typography-700">
                  {row.externalProbability === null
                    ? "—"
                    : `${formatPercent(row.externalProbability)} ${row.leftParty}`}
                </Text>
              )}
            </Pressable>
          ))}
        </VStack>
      </VStack>
    </Card>
  );
};

export default RaceList;
